import {
  Button,
  ButtonGroup,
  Disclosure,
  DisclosurePanel,
  DisclosureTrigger,
  LinkButton,
  Stack,
  Typography,
} from '@compass/components'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import { AppRoute } from 'appRoutes'
import { SmartSignalsUpsellBanner } from 'components/SmartSignalsUpsellBanner/SmartSignalsUpsellBanner'
import { DOCS_SMART_SIGNALS_REFERENCE } from 'const'
import { GenericEmptyState } from 'features/commonUI'
import { UpgradeModal } from 'features/planManagement'
import { useCurrentSubscription } from 'features/subscription'
import { formatJSON } from 'helpers/format'
import { getDisabledProductNames, SIGNAL_DEFINITION_KEY_TO_APP_PRODUCT } from 'helpers/smartSignalMappings'
import { useSmartSignalsEnabled } from 'hooks/api/smart_signals'
import {
  ExternalLinkIcon,
  FileSearch,
  InfoIcon,
  MonitorIcon,
  SmartphoneIcon,
  TriangleAlertIcon,
  ZapIcon,
} from 'lucide-react'
import { ampli } from 'models/ampli'
import { IdEventV4, SDKPlatform } from 'models/event'
import { ComponentProps, forwardRef, Ref, useMemo, useState } from 'react'

import { CodeViewerModal } from './CodeViewerModal'
import { SMART_SIGNAL_DEFINITIONS } from './definitions'
import { SmartSignalDefinition } from './definitions/SmartSignalRepresentation'

type SignalFilterKey = 'all' | 'detected' | 'web' | 'mobile'

const HIDDEN_SIGNAL_FILTER_KEYS_MAP: Record<SDKPlatform, ComponentProps<typeof SmartSignalSelector>['hidden']> = {
  js: 'mobile',
  ios: 'web',
  android: 'web',
  unknown: undefined,
}

function SmartSignalDetection_({ event }: { event?: IdEventV4 }, ref?: Ref<HTMLAnchorElement>) {
  const { currentSubscriptionId } = useCurrentSubscription()
  const { data: enabledProducts } = useSmartSignalsEnabled(currentSubscriptionId)

  const enabledDefinitions = useMemo(() => {
    if (!enabledProducts) {
      return SMART_SIGNAL_DEFINITIONS
    }
    const enabledSet = new Set(enabledProducts)
    return SMART_SIGNAL_DEFINITIONS.filter((d) => {
      const product = SIGNAL_DEFINITION_KEY_TO_APP_PRODUCT[d.key]
      return product == null || enabledSet.has(product)
    })
  }, [enabledProducts])

  const disabledSignalNames = useMemo(() => {
    if (!enabledProducts || enabledProducts.length === 0) {
      return []
    }
    const signalProducts = [...new Set(Object.values(SIGNAL_DEFINITION_KEY_TO_APP_PRODUCT))]
    return getDisabledProductNames(enabledProducts, signalProducts)
  }, [enabledProducts])

  const all = event ? enabledDefinitions.filter((d) => d.extract(event)) : []
  const detected = event ? all.filter((d) => d.isDetected(event)) : []
  const web = event ? all.filter((d) => d.isWeb(event)) : []
  const mobile = event ? all.filter((d) => d.isMobile(event)) : []

  const selections = { all, detected, web, mobile }
  const [selected, setSelected] = useState<SignalFilterKey>('detected')
  const rows = selections[selected]

  const eventPlatform = event?.sdk?.platform

  return (
    <>
      <div className='relative w-full'>
        <a id='smart-signals' className='absolute -top-20' ref={ref} />
        <SmartSignalSelector
          selected={selected}
          setSelected={(tabName) => {
            setSelected(tabName)
            ampli.identificationEventDetailsSmartSignalsTabViewed({ tabName: tabName })
          }}
          selections={selections}
          hidden={HIDDEN_SIGNAL_FILTER_KEYS_MAP[eventPlatform ?? 'unknown']}
        />
      </div>
      {enabledProducts && enabledProducts.length > 0 && disabledSignalNames.length > 0 && (
        <SmartSignalsUpsellBanner
          intro='Add deeper context with more Smart Signals like'
          disabledSignalNames={disabledSignalNames}
          pagePath={AppRoute.IdentificationEvents}
        />
      )}
      {event == null ? (
        <Paper>
          <SmartSignalRowsLoading />
        </Paper>
      ) : null}

      {event != null && all.length === 0 ? <SmartSignalsUpsell /> : null}

      {event !== null && all.length > 0 && rows.length === 0 ? (
        <Paper className='p-8 flex flex-col gap-2 items-center'>
          <InfoIcon size={16} className='text-gray-600' />
          <Typography size='sm' className='text-gray-800'>
            No Smart Signals were detected for this event...
          </Typography>
        </Paper>
      ) : null}

      {event != null && all.length > 0 && rows.length > 0 ? (
        <Paper>
          {rows.map((row, i) => (
            <SmartSignalRow
              key={`${selected}-${row.key}`}
              row={row}
              event={event}
              isOpenByDefault={selected === 'detected'}
              last={i === rows.length - 1}
            />
          ))}
        </Paper>
      ) : null}
    </>
  )
}

function SmartSignalRowsLoading() {
  return (
    <>
      <div className='p-4 border-b border-gray-300 flex gap-4'>
        <Skeleton height={16} width={16} className='transform-none!' />
        <Skeleton height={16} width={192} className='transform-none!' />
      </div>
      <div className='p-4 border-b border-gray-300 flex gap-4'>
        <Skeleton height={16} width={16} className='transform-none!' />
        <Skeleton height={16} width={80} className='transform-none!' />
      </div>
      <div className='p-4 flex gap-4'>
        <Skeleton height={16} width={16} className='transform-none!' />
        <Skeleton height={16} width={192} className='transform-none!' />
      </div>
    </>
  )
}

function SmartSignalRow({
  row,
  event,
  isOpenByDefault,
  last = false,
}: {
  row: SmartSignalDefinition<any>
  event: IdEventV4
  isOpenByDefault: boolean
  last?: boolean
}) {
  return (
    <Disclosure key={row.key} defaultExpanded={isOpenByDefault} className={last ? 'border-none' : ''}>
      <DisclosureTrigger className='p-4'>
        <div className='flex flex-row gap-4 items-center'>
          {typeof row.icon === 'function' ? row.icon(event) : row.icon}
          <span className='flex flex-row gap-2 items-center'>
            <Typography size='base' weight='medium'>
              {row.label}
            </Typography>
          </span>
        </div>
      </DisclosureTrigger>
      <DisclosurePanel>
        <Stack direction='column' gap={4} className='px-4 pb-4'>
          {typeof row.details === 'function' ? row.details(event) : row.details}
          {row.searchFor || row.documentationHash ? (
            <Stack gap={3}>
              {row.searchFor ? (
                <CodeViewerModal value={formatJSON(event)} defaultSearch={row.searchFor}>
                  <Button
                    variant='secondary'
                    onPress={() => {
                      ampli.smartSignalJsonLookupSelected({ signalName: row.key })
                    }}
                  >
                    <FileSearch /> Lookup in JSON
                  </Button>
                </CodeViewerModal>
              ) : null}
              {row.documentationHash ? (
                <LinkButton
                  alt
                  variant='ghost'
                  target='_blank'
                  href={DOCS_SMART_SIGNALS_REFERENCE + row.documentationHash}
                >
                  See signal in docs <ExternalLinkIcon />
                </LinkButton>
              ) : null}
            </Stack>
          ) : null}
        </Stack>
      </DisclosurePanel>
    </Disclosure>
  )
}

function SmartSignalsUpsell() {
  const { currentSubscriptionId: subscriptionId } = useCurrentSubscription()
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false)

  return (
    <Paper>
      <GenericEmptyState
        icon={<ZapIcon />}
        title='View Smart Signals about this event'
        description='Uncover device intelligence including VPN, bot detection, and more.'
        buttonProps={{
          children: 'Reveal Smart Signals',
          onPress: () => setIsUpgradeModalOpen(true),
        }}
      />
      <UpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        subscriptionId={subscriptionId}
      />
    </Paper>
  )
}

export const SmartSignalDetection = forwardRef(SmartSignalDetection_)

function SmartSignalSelector_(
  {
    selected,
    setSelected,
    selections,
    hidden,
  }: {
    selected: SignalFilterKey
    setSelected: (k: SignalFilterKey) => void
    selections: Record<SignalFilterKey, readonly SmartSignalDefinition<any>[]>
    hidden: Extract<SignalFilterKey, 'web' | 'mobile'> | undefined
  },
  ref?: Ref<HTMLDivElement>
) {
  const isDisabled =
    selections.all.length === 0 &&
    selections.detected.length === 0 &&
    selections.web.length === 0 &&
    selections.mobile.length === 0

  return (
    <ButtonGroup fullWidth ref={ref}>
      <Button
        size='default'
        variant='secondary'
        tone={selected === 'all' ? 'background2' : undefined}
        onPress={() => setSelected('all')}
        aria-label='Show every Smart Signal'
        isDisabled={isDisabled}
        className={selected === 'all' ? '[&_svg]:text-current shadow-inset-t-black-200' : undefined}
        alt={selected !== 'all'}
      >
        All
        <span className='text-gray-800 font-normal'>{selections.all.length}</span>
      </Button>
      <Button
        size='default'
        variant='secondary'
        tone={selected === 'detected' ? 'background2' : undefined}
        onPress={() => setSelected('detected')}
        aria-label='Show detected Smart Signals'
        isDisabled={isDisabled}
        className={selected === 'detected' ? '[&_svg]:text-current shadow-inset-t-black-200' : undefined}
        alt={selected !== 'detected'}
      >
        <TriangleAlertIcon size={16} className={`${selections.detected.length > 0 ? 'text-red-800!' : ''}`} />
        <span className='hidden md:inline'>Detected</span>
        <span className='text-gray-800 font-normal'>{selections.detected.length}</span>
      </Button>
      {hidden === 'web' ? null : (
        <Button
          size='default'
          variant='secondary'
          tone={selected === 'web' ? 'background2' : undefined}
          onPress={() => setSelected('web')}
          aria-label='Show web Smart Signals'
          isDisabled={isDisabled}
          className={selected === 'web' ? '[&_svg]:text-current shadow-inset-t-black-200' : undefined}
          alt={selected !== 'web'}
        >
          <MonitorIcon size={16} />
          <span className='hidden md:inline'>Web</span>
          <span className='text-gray-800 font-normal'>{selections.web.length}</span>
        </Button>
      )}
      {hidden === 'mobile' ? null : (
        <Button
          size='default'
          variant='secondary'
          tone={selected === 'mobile' ? 'background2' : undefined}
          onPress={() => setSelected('mobile')}
          aria-label='Show mobile Smart Signals'
          isDisabled={isDisabled}
          className={selected === 'mobile' ? '[&_svg]:text-current shadow-inset-t-black-200' : undefined}
          alt={selected !== 'mobile'}
        >
          <SmartphoneIcon size={16} />
          <span className='hidden md:inline'>Mobile</span>
          <span className='text-gray-800 font-normal'>{selections.mobile.length}</span>
        </Button>
      )}
    </ButtonGroup>
  )
}

export const SmartSignalSelector = forwardRef(SmartSignalSelector_)
